import React, { useState, useEffect } from 'react';
import {
    EditButton,
    Create,
    Edit,
    SimpleForm,
    TextInput,
    SelectInput,
    ReferenceInput,
    required,
    useNotify,
    useRefresh,
    Confirm
} from 'react-admin';
import { Reviews, Delete as DeleteIcon } from '@mui/icons-material';
import { Button } from '@mui/material';
import SortableTable from './components/SortableTable';
import { httpClient, publicBase } from './dataProvider';
import { Link } from 'react-router-dom';

export const CourseReviewNoteIcon = Reviews;

const DeleteReviewButton = ({ record }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleClick = () => setOpen(true);
    const handleDialogClose = () => setOpen(false);

    const handleConfirm = async () => {
        setLoading(true);
        try {
            const url = `${publicBase}/course_review_notes/${record.id}`;
            await httpClient(url, { method: 'DELETE' });
            notify('Review note deleted successfully', { type: 'success' });
            refresh();
            window.location.reload();
        } catch (error) {
            console.error('Error deleting review note:', error);
            notify('Failed to delete review note', { type: 'error' });
        } finally {
            setLoading(false);
            setOpen(false);
        }
    };

    return (
        <>
            <Button
                onClick={handleClick}
                color="error"
                startIcon={<DeleteIcon />}
            >
                Delete
            </Button>
            <Confirm
                isOpen={open}
                loading={loading}
                title="Delete Review Note"
                content="Are you sure you want to delete this review note? This action cannot be undone."
                onConfirm={handleConfirm}
                onClose={handleDialogClose}
            />
        </>
    );
};

export const CourseReviewNoteList = () => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const url = `${publicBase}/course_review_notes?end=1000`; // Fetch all for client-side sort
        httpClient(url)
            .then(({ json }) => {
                setReviews(json || []);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching review notes:', error);
                setLoading(false);
            });
    }, []);

    const columns = [
        { key: 'user_email', header: 'User' },
        { key: 'course_title', header: 'Course' },
        { key: 'material_type', header: 'Material Type' },
        { key: 'review_text', header: 'Review Text' },
        {
            key: 'created_at',
            header: 'Created At',
            render: (record) => new Date(record.created_at).toLocaleString()
        },
        {
            key: 'actions',
            header: 'Actions',
            sortable: false,
            render: (record) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <EditButton record={record} resource="course_review_notes" />
                    <DeleteReviewButton record={record} />
                </div>
            )
        }
    ];

    if (loading) return <p>Loading...</p>;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2>Course Review Notes</h2>
                <Link
                    to="/course_review_notes/create"
                    style={{
                        padding: '8px 16px',
                        backgroundColor: '#1976d2',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: '500'
                    }}
                >
                    Create
                </Link>
            </div>
            <SortableTable data={reviews} columns={columns} />
        </div>
    );
};

export const CourseReviewNoteEdit = () => (
    <Edit title="Edit Course Review">
        <SimpleForm>
            <ReferenceInput source="user_id" reference="users" label="User">
                <SelectInput optionText="email" validate={required()} />
            </ReferenceInput>
            <ReferenceInput source="course_id" reference="courses" label="Course">
                <SelectInput optionText="title" validate={required()} />
            </ReferenceInput>
            <SelectInput source="material_type" choices={[
                { id: 'textbook', name: 'Textbook' },
                { id: 'podcast', name: 'Podcast' },
                { id: 'video', name: 'Video' },
            ]} validate={required()} />
            <TextInput source="review_text" multiline fullWidth validate={required()} />
        </SimpleForm>
    </Edit>
);

export const CourseReviewNoteCreate = () => (
    <Create title="Create Course Review">
        <SimpleForm>
            <ReferenceInput source="user_id" reference="users" label="User">
                <SelectInput optionText="email" validate={required()} />
            </ReferenceInput>
            <ReferenceInput source="course_id" reference="courses" label="Course">
                <SelectInput optionText="title" validate={required()} />
            </ReferenceInput>
            <SelectInput source="material_type" choices={[
                { id: 'textbook', name: 'Textbook' },
                { id: 'podcast', name: 'Podcast' },
                { id: 'video', name: 'Video' },
            ]} validate={required()} />
            <TextInput source="review_text" multiline fullWidth validate={required()} />
        </SimpleForm>
    </Create>
);
